/* eslint-disable space-before-function-paren */
/* eslint-disable padded-blocks */

/**
 * Video Play Button Handler
 * Adds a custom play button overlay that triggers video playback,
 * then hands control to native video controls
 */
const playButtonSvg = '<svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="48" cy="48" r="48" fill="#A8E0F9"/><path d="M66 44.5359C68.6667 46.0755 68.6667 49.9245 66 51.4641L42 65.3205C39.3333 66.8601 36 64.9356 36 61.8564L36 34.1436C36 31.0644 39.3333 29.1399 42 30.6795L66 44.5359Z" fill="#025FFE"/></svg>'

// Debug flag - set to true to enable console logging
const DEBUG_VIDEO = true

/**
 * Add URL parameters to hide video branding (title, logo, channel)
 * while keeping native controls as-is
 */
function addCleanParametersToUrl(url) {
    const isVimeo = url.includes('vimeo.com')
    const isYouTube = url.includes('youtube.com') || url.includes('youtu.be')

    if (!isVimeo && !isYouTube) return url

    // Parameters to hide branding but keep controls
    const params = isVimeo
        ? { 'title': '0', 'byline': '0', 'portrait': '0' }
        : { 'modestbranding': '1', 'rel': '0', 'enablejsapi': '1' } // enablejsapi required for postMessage API

    // Parse URL
    let [baseUrl, hash] = url.split('#')
    let [urlWithoutQuery, queryString] = baseUrl.split('?')

    const existingParams = new URLSearchParams(queryString || '')

    // Add parameters only if they don't exist (don't override CMS values)
    Object.keys(params).forEach(key => {
        if (!existingParams.has(key)) {
            existingParams.set(key, params[key])
        }
    })

    const newQueryString = existingParams.toString()
    return urlWithoutQuery + (newQueryString ? '?' + newQueryString : '') + (hash ? '#' + hash : '')
}

function injectPlayButtons() {
    // Find all video containers
    const videoContainers = document.querySelectorAll('.video-container')

    if (DEBUG_VIDEO) {
        console.log('[Video] Found', videoContainers.length, 'video container(s)')
    }

    videoContainers.forEach(container => {
        // Skip if play button already exists
        if (container.querySelector('.video-play-button')) {
            return
        }

        // Check if container has an iframe
        const iframe = container.querySelector('iframe')
        if (!iframe) return

        // Check if it's a YouTube or Vimeo video
        let src = iframe.getAttribute('src') || ''
        if (!src.includes('youtube.com') && !src.includes('youtu.be') && !src.includes('vimeo.com')) {
            return
        }

        const videoType = src.includes('vimeo.com') ? 'Vimeo' : 'YouTube'
        if (DEBUG_VIDEO) {
            console.log('[Video] Processing', videoType, 'video')
            console.log('[Video] Original src:', src)
        }

        // Add parameters to hide branding (title, logo, channel) but keep controls as-is
        src = addCleanParametersToUrl(src)
        iframe.setAttribute('src', src)

        if (DEBUG_VIDEO) {
            console.log('[Video] Updated src (branding hidden):', src)
            console.log('[Video] Has controls param:', src.includes('controls'))
        }

        // Ensure iframe has proper accessibility and permission attributes
        let iframeTitle = iframe.getAttribute('title') || iframe.getAttribute('aria-label')
        if (!iframeTitle) {
            iframeTitle = 'Video player'
        }
        iframe.setAttribute('title', iframeTitle)
        iframe.setAttribute('aria-label', iframeTitle)

        // Add allow attribute for autoplay support
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture')

        // Create and inject play button overlay
        const playButton = document.createElement('button')
        playButton.className = 'video-play-button'
        playButton.type = 'button'
        playButton.setAttribute('aria-label', `Play ${iframeTitle}`)
        playButton.innerHTML = playButtonSvg

        container.insertBefore(playButton, container.firstChild)

        if (DEBUG_VIDEO) {
            console.log('[Video] Injected custom play button overlay')
        }
    })
}

function initVideoPlayButtons() {
    // Listen for postMessage responses from video players (for debugging)
    if (DEBUG_VIDEO) {
        window.addEventListener('message', function(event) {
            if (event.origin === 'https://player.vimeo.com' || event.origin === 'https://www.youtube.com') {
                console.log('[Video] Message from player:', event.origin, event.data)
            }
        })
        console.log('[Video] Debug mode enabled - listening for player messages')
    }

    // Inject play buttons into video containers
    injectPlayButtons()

    // Set up click handlers
    const playButtons = document.querySelectorAll('.video-play-button')

    playButtons.forEach(button => {
        // Store YouTube player ready state
        const videoContainer = button.closest('.video-container')
        if (!videoContainer) return

        const iframe = videoContainer.querySelector('iframe')
        if (!iframe) return

        const currentSrc = iframe.getAttribute('src') || ''
        const isYouTube = currentSrc.includes('youtube.com') || currentSrc.includes('youtu.be')

        // For YouTube, listen for player ready event
        if (isYouTube) {
            let youtubeReady = false

            const messageHandler = (event) => {
                if (event.origin !== 'https://www.youtube.com') return

                try {
                    const data = JSON.parse(event.data)
                    if (data.event === 'onReady') {
                        youtubeReady = true
                        if (DEBUG_VIDEO) console.log('[Video] YouTube player ready')
                    }
                } catch (e) {
                    // Ignore parsing errors
                }
            }

            window.addEventListener('message', messageHandler)
            button.dataset.youtubeReady = 'false'

            // Store ready state on button
            const checkReady = setInterval(() => {
                if (youtubeReady) {
                    button.dataset.youtubeReady = 'true'
                    clearInterval(checkReady)
                }
            }, 100)
        }

        button.addEventListener('click', function(e) {
            e.stopPropagation()

            const videoContainer = button.closest('.video-container')
            if (!videoContainer) return

            const iframe = videoContainer.querySelector('iframe')
            if (!iframe) return

            const currentSrc = iframe.getAttribute('src') || ''
            const videoType = currentSrc.includes('vimeo.com') ? 'Vimeo' : 'YouTube'

            if (DEBUG_VIDEO) {
                console.log('[Video] Play button clicked')
                console.log('[Video] Video type:', videoType)
                console.log('[Video] Current iframe src:', currentSrc)
                if (videoType === 'YouTube') {
                    console.log('[Video] YouTube ready:', button.dataset.youtubeReady)
                }
            }

            // Send play command via postMessage API
            // Add delay to ensure iframe player is ready, especially on production
            if (currentSrc.includes('vimeo.com')) {
                try {
                    if (DEBUG_VIDEO) console.log('[Video] Sending unmute + play commands to Vimeo')

                    // Explicitly set volume to 1 (unmuted) before playing
                    iframe.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*')

                    // Small delay to ensure volume is set before playing
                    setTimeout(() => {
                        iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*')
                    }, 100)
                } catch (e) {
                    console.log('Could not control Vimeo player:', e)
                }
            } else if (currentSrc.includes('youtube.com') || currentSrc.includes('youtu.be')) {
                const sendYouTubeCommands = () => {
                    try {
                        if (DEBUG_VIDEO) console.log('[Video] Sending unmute + play commands to YouTube')

                        // Explicitly unmute before playing
                        iframe.contentWindow.postMessage(JSON.stringify({
                            event: 'command',
                            func: 'unMute'
                        }), '*')

                        // Small delay to ensure unmute is processed before playing
                        setTimeout(() => {
                            iframe.contentWindow.postMessage(JSON.stringify({
                                event: 'command',
                                func: 'playVideo'
                            }), '*')
                        }, 100)
                    } catch (e) {
                        console.log('Could not control YouTube player:', e)
                    }
                }

                // Check if YouTube player is ready
                if (button.dataset.youtubeReady === 'true') {
                    sendYouTubeCommands()
                } else {
                    if (DEBUG_VIDEO) console.log('[Video] YouTube not ready yet, waiting...')
                    // Wait for player to be ready (max 3 seconds)
                    let attempts = 0
                    const waitForReady = setInterval(() => {
                        attempts++
                        if (button.dataset.youtubeReady === 'true') {
                            if (DEBUG_VIDEO) console.log('[Video] YouTube ready after waiting')
                            clearInterval(waitForReady)
                            sendYouTubeCommands()
                        } else if (attempts > 30) {
                            if (DEBUG_VIDEO) console.log('[Video] YouTube timeout - sending commands anyway')
                            clearInterval(waitForReady)
                            sendYouTubeCommands()
                        }
                    }, 100)
                }
            }

            // Hide the custom play button - native controls take over
            button.classList.add('-hidden')

            if (DEBUG_VIDEO) {
                console.log('[Video] Custom button hidden, native controls active')
                console.log('[Video] Button has -hidden class:', button.classList.contains('-hidden'))
                console.log('[Video] Iframe still has src:', iframe.getAttribute('src').substring(0, 80) + '...')
            }
        })
    })
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoPlayButtons)
} else {
    initVideoPlayButtons()
}

export default initVideoPlayButtons
